<template>
  <!-- Google and Microsoft side by side, each with its own mark in its brand
       colours (the providers' sign-in branding rules). One column on a narrow phone. -->
  <div class="si-providers" :class="{ 'si-providers--single': providers.length === 1 }">
    <v-btn
      v-for="provider in providers"
      :key="provider"
      class="si-providers__btn"
      :data-test="`provider-${provider}`"
      :aria-label="provider === 'google' ? t('auth.identity.continueWithGoogle') : t('auth.identity.continueWithMicrosoft')"
      variant="outlined"
      height="48"
      block
      :loading="busy === provider"
      :disabled="!!busy && busy !== provider"
      @click="$emit('pick', provider)"
    >
      <template #prepend>
        <svg v-if="provider === 'google'" class="si-providers__mark" :data-test="`provider-mark-${provider}`" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
        <svg v-else class="si-providers__mark" :data-test="`provider-mark-${provider}`" viewBox="0 0 21 21" aria-hidden="true">
          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
      </template>
      {{ provider === "google" ? "Google" : "Microsoft" }}
    </v-btn>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { SocialProvider } from "@/auth/identity";

export default defineComponent({
  name: "ProviderButtons",
  props: {
    providers: { type: Array as PropType<SocialProvider[]>, required: true },
    /** The action in progress in the form, if any. */
    busy: { type: String as PropType<string | null>, default: null },
  },
  emits: {
    pick: (provider: SocialProvider) => !!provider,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
});
</script>

<style scoped>
.si-providers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.si-providers--single {
  grid-template-columns: minmax(0, 1fr);
}
/* Vuetify's `block` sets min-width:100%. iOS Safari resolves a percentage
   min-width on a grid item against the whole grid, not the column, so each
   button became full-grid wide and the pair ran off the card. */
.si-providers .si-providers__btn {
  width: 100%;
  min-width: 0;
}
.si-providers__btn {
  border-radius: 12px;
  border-color: rgba(var(--v-theme-on-surface), 0.18);
  background: rgb(var(--v-theme-surface));
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
.si-providers__mark {
  width: 18px;
  height: 18px;
  flex: none;
}
@media (max-width: 380px) {
  .si-providers {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
